import { Users, Pill } from 'lucide-react';
import useRegistrationStore from '../../store/useRegistrationStore';
import { useEffect } from 'react';

const PatientCapacityStep = () => {
    const { formData, updateFormData, providerData } = useRegistrationStore();

    const isPharmacy = providerData?.providerType === 2;

    useEffect(() => {
        if (isPharmacy) {
            // Only set to 0 if they don't already have values
            if (formData.patientCapacityOP === undefined) {
                updateFormData({
                    patientCapacityOP: 0,
                    patientCapacityIP: 0,
                    patientCapacityDC: 0,
                });
            }
        } else {
            // Only set to empty if they don't already have a value
            if (formData.discount === undefined) {
                updateFormData({ discount: '' });
            }
        }
    }, [isPharmacy, formData.patientCapacityOP, formData.discount, updateFormData]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        // Allow empty string for better UX
        if (value === '') {
            updateFormData({ [name]: '' });
            return;
        }

        const numValue = parseInt(value, 10);

        // Validate the input is a valid number
        if (isNaN(numValue) || numValue < 0) {
            return;
        }

        // Enforce 0-100 for discount
        if (name === 'discount') {
            updateFormData({ [name]: Math.min(100, numValue) });
        } else {
            updateFormData({ [name]: numValue });
        }
    };

    const handleBlur = (e) => {
        const { name, value } = e.target;
        // If field is empty on blur, set to 0
        if (value === '') {
            updateFormData({ [name]: 0 });
        }
    };

    return (
        <div className="space-y-10 max-w-5xl mx-auto px-4">
            {/* Header */}
            <div className="flex items-center gap-5">
                <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center">
                    {isPharmacy ? (
                        <Pill className="w-7 h-7 text-primary" />
                    ) : (
                        <Users className="w-7 h-7 text-primary" />
                    )}
                </div>
                <div>
                    <h2 className="text-3xl font-bold text-gray-900">
                        {isPharmacy ? 'Pharmacy Discount' : 'Patient Capacity'}
                    </h2>
                    <p className="text-lg text-gray-600 mt-1.5">
                        {isPharmacy
                            ? ''
                            : 'Enter the average daily patient handling capacity of your facility'}
                    </p>
                </div>
            </div>

            {/* Main Content Card */}
            <div className="bg-white border border-gray-200 rounded-3xl p-8 shadow-lg">
                {isPharmacy ? (
                    // Pharmacy-only
                    <div className="max-w-md mx-auto">
                        <div className="space-y-3">
                            <label className="block text-xl font-semibold text-gray-800">
                                Pharmacy Discount Percentage
                            </label>
                            <div className="relative">
                                <input
                                    type="number"
                                    name="discount"
                                    value={formData.discount === 0 || formData.discount ? formData.discount : ''}
                                    onChange={handleInputChange}
                                    onBlur={handleBlur}
                                    placeholder="Enter discount (0-100)"
                                    min="0"
                                    max="100"
                                    className="w-full px-6 py-5 text-xl font-medium border-2 border-gray-300 rounded-2xl focus:border-primary focus:ring-4 focus:ring-primary/20 outline-none transition placeholder:text-gray-400"
                                    style={{
                                        MozAppearance: 'textfield',
                                        WebkitAppearance: 'none',
                                    }}
                                />
                                <span className="absolute right-6 top-1/2 -translate-y-1/2 text-xl font-semibold text-gray-500 pointer-events-none">
                                    %
                                </span>
                            </div>

                        </div>
                    </div>
                ) : (
                    // Non-pharmacy (hospitals/clinics) - only capacities
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                        {/* OP Capacity */}
                        <div className="space-y-3">
                            <label className="block text-lg font-semibold text-gray-800">
                                Outpatient (OP) Capacity
                            </label>
                            <div className="relative">
                                <input
                                    type="number"
                                    name="patientCapacityOP"
                                    value={formData.patientCapacityOP === 0 || formData.patientCapacityOP ? formData.patientCapacityOP : ''}
                                    onChange={handleInputChange}
                                    onBlur={handleBlur}
                                    placeholder="Enter capacity"
                                    min="0"
                                    className="w-full px-6 py-4 pr-32 text-lg border-2 border-gray-300 rounded-2xl focus:border-primary focus:ring-4 focus:ring-primary/20 outline-none transition placeholder:text-gray-400"
                                    style={{
                                        MozAppearance: 'textfield',
                                        WebkitAppearance: 'none',
                                    }}
                                />
                                <span className="absolute right-5 top-1/2 -translate-y-1/2 text-sm text-gray-500 pointer-events-none whitespace-nowrap">
                                    patients/day
                                </span>
                            </div>
                        </div>

                        {/* IP Capacity */}
                        <div className="space-y-3">
                            <label className="block text-lg font-semibold text-gray-800">
                                Inpatient (IP) Capacity
                            </label>
                            <div className="relative">
                                <input
                                    type="number"
                                    name="patientCapacityIP"
                                    value={formData.patientCapacityIP === 0 || formData.patientCapacityIP ? formData.patientCapacityIP : ''}
                                    onChange={handleInputChange}
                                    onBlur={handleBlur}
                                    placeholder="Enter beds"
                                    min="0"
                                    className="w-full px-6 py-4 pr-20 text-lg border-2 border-gray-300 rounded-2xl focus:border-primary focus:ring-4 focus:ring-primary/20 outline-none transition placeholder:text-gray-400"
                                    style={{
                                        MozAppearance: 'textfield',
                                        WebkitAppearance: 'none',
                                    }}
                                />
                                <span className="absolute right-5 top-1/2 -translate-y-1/2 text-sm text-gray-500 pointer-events-none">
                                    beds
                                </span>
                            </div>
                        </div>

                        {/* DC Capacity */}
                        <div className="space-y-3">
                            <label className="block text-lg font-semibold text-gray-800">
                                Day Care Capacity
                            </label>
                            <div className="relative">
                                <input
                                    type="number"
                                    name="patientCapacityDC"
                                    value={formData.patientCapacityDC === 0 || formData.patientCapacityDC ? formData.patientCapacityDC : ''}
                                    onChange={handleInputChange}
                                    onBlur={handleBlur}
                                    placeholder="Enter capacity"
                                    min="0"
                                    className="w-full px-6 py-4 pr-32 text-lg border-2 border-gray-300 rounded-2xl focus:border-primary focus:ring-4 focus:ring-primary/20 outline-none transition placeholder:text-gray-400"
                                    style={{
                                        MozAppearance: 'textfield',
                                        WebkitAppearance: 'none',
                                    }}
                                />
                                <span className="absolute right-5 top-1/2 -translate-y-1/2 text-sm text-gray-500 pointer-events-none whitespace-nowrap">
                                    patients/day
                                </span>
                            </div>
                        </div>
                    </div>
                )}


            </div>
        </div>
    );
};

export default PatientCapacityStep;